/**
 * Sidebar drop handler — §16 DB operations for all sidebar drop targets.
 *
 * Called when a task (single or multi-drag) is dropped onto a sidebar item.
 * Handles the full lifecycle:
 *
 * 1. **Reverse source state** if task comes from Trash (restore) or Logbook (uncomplete)
 * 2. **Apply target behavior** per the drop-target table in §16
 *
 * Self-drops (Trash → Trash, Logbook → Logbook) are no-ops.
 *
 * @see docs/plans/view-and-dnd-master-plan.md §16
 */

import { toast } from "sonner"
import {
  db,
  ObjectStatus,
  ObjectType,
  StartAtType,
  StartAtSubType,
} from "@/db/schema"
import { now } from "@/shared/utils/date/dateUtils"
import {
  moveTask,
  moveMultiple,
  scheduleTask,
  clearSchedule,
  completeTask,
  deleteTask,
  restoreFromTrash,
  bulkComplete,
  bulkDelete,
  bulkUncomplete,
} from "@/db/operations"
import type { InsertPosition } from "@/db/operations"
import { useViewContext } from "@/app/stores/viewContext"
import { setContainerStatuses } from "@/features/project-header/projectStatusRequest"
import { canDropOnSidebarTarget, buildDragSourceInfo } from "./dndConstraints"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Valid sidebar drop targets.
 * Static targets use simple strings; container targets use "type:id" format.
 */
export type SidebarDropTarget =
  | "inbox"
  | "today"
  | "upcoming"
  | "anyday"
  | "someday"
  | "logbook"
  | "trash"
  | "this-evening"
  | `project:${string}`
  | `list:${string}`
  | `area:${string}`

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Handle a drop onto a sidebar target.
 *
 * @param target — Sidebar drop target identifier
 * @param taskIds — IDs of dragged tasks (single or multi-drag)
 */
export async function handleSidebarDrop(
  target: string,
  taskIds: string[],
): Promise<void> {
  if (taskIds.length === 0) return

  // ── Special case: Upcoming → open SchedulePicker, don't process further ──
  if (target === "upcoming") {
    const el = document.querySelector('[data-sidebar-drop="upcoming"]')
    const anchor = el?.getBoundingClientRect()
    useViewContext.getState().openSchedulePicker(taskIds, anchor ?? undefined)
    return
  }

  try {
    // §19: Detect sections in the dragged set — sections are structural
    // elements that need special treatment for date and inbox targets.
    const allObjects = await db.totodo.bulkGet(taskIds)

    // §19 Safety guard: validate drop against constraint matrix
    const allTypes = allObjects.map((obj) => obj?.type ?? ObjectType.Task)
    const sourceInfo = buildDragSourceInfo(allTypes)
    if (!canDropOnSidebarTarget(sourceInfo, target)) {
      console.debug(
        `[sidebarDropHandler] Rejected drop: ${sourceInfo.type} → ${target}`,
      )
      return
    }

    const sectionIds = new Set<string>()
    const containerItemIds = new Set<string>()
    for (const obj of allObjects) {
      if (obj?.type === ObjectType.Section && obj.id) {
        sectionIds.add(obj.id)
      }
      // Projects/Lists cannot be nested into containers via sidebar drop
      if (
        (obj?.type === ObjectType.Project || obj?.type === ObjectType.List) &&
        obj.id
      ) {
        containerItemIds.add(obj.id)
      }
    }
    const hasSections = sectionIds.size > 0
    const taskOnlyIds = hasSections
      ? taskIds.filter((id) => !sectionIds.has(id))
      : taskIds
    const sectionIdArr = hasSections ? [...sectionIds] : []

    // Filter out projects/lists for container targets (can't nest containers)
    const droppableIds =
      containerItemIds.size > 0
        ? taskIds.filter((id) => !containerItemIds.has(id))
        : taskIds

    // Phase 1: Reverse source state (restore from trash / uncomplete from logbook)
    // Only for non-section tasks — sections don't have status/completion.
    if (taskOnlyIds.length > 0) {
      await prepareTasksForDrop(taskOnlyIds, target)
    }

    // Phase 2: Apply target behavior
    // §19 rules:
    //   Date targets (today/anyday/someday): schedule children only, skip sections
    //   Inbox: move children to inbox, soft-delete sections
    //   Container: move all (section + children)
    //   Logbook: complete children, soft-delete sections
    //   Trash: delete all including sections
    if (target === "inbox") {
      if (taskOnlyIds.length > 0) await dropToInbox(taskOnlyIds)
      // Sections lose their children → soft-delete them
      if (hasSections) await bulkDelete(sectionIdArr)
    } else if (target === "today") {
      if (taskOnlyIds.length > 0) await dropToToday(taskOnlyIds)
      // Sections are structural, not scheduled — no-op for them
    } else if (target === "this-evening") {
      if (taskOnlyIds.length > 0) await dropToThisEvening(taskOnlyIds)
      // Sections are structural, not scheduled — no-op for them
    } else if (target === "anyday") {
      if (taskOnlyIds.length > 0) await dropToAnyday(taskOnlyIds)
    } else if (target === "someday") {
      if (taskOnlyIds.length > 0) await dropToSomeday(taskOnlyIds)
    } else if (target === "logbook") {
      if (taskOnlyIds.length > 0) await dropToLogbook(taskOnlyIds)
      // Sections can't be "completed" — soft-delete them
      if (hasSections) await bulkDelete(sectionIdArr)
    } else if (target === "trash") {
      // Delete everything including sections
      await dropToTrash(taskIds)
    } else if (
      target.startsWith("project:") ||
      target.startsWith("list:") ||
      target.startsWith("area:")
    ) {
      // Move all except containers — section + children go together
      const containerId = target.split(":")[1]!
      if (droppableIds.length > 0) {
        await dropToContainer(droppableIds, containerId)
      }
    }
  } catch (error) {
    console.error("[sidebarDropHandler] Drop failed:", error)
  }
}

// ---------------------------------------------------------------------------
// Phase 1: Prepare — restore/uncomplete as needed
// ---------------------------------------------------------------------------

/**
 * If tasks come from Trash or Logbook, reverse that state first.
 *
 * - isDeleted=1 && target !== "trash" → restoreFromTrash
 * - status=Completed && target !== "logbook" → uncomplete
 */
async function prepareTasksForDrop(
  taskIds: string[],
  target: string,
): Promise<void> {
  const tasks = await db.totodo.bulkGet(taskIds)

  // Collect IDs that need restoration/uncomplete
  const needRestore: string[] = []
  const needUncomplete: string[] = []

  for (const task of tasks) {
    if (!task?.id) continue
    if (task.isDeleted === 1 && target !== "trash") {
      needRestore.push(task.id)
    }
    if (task.status === ObjectStatus.Completed && target !== "logbook") {
      needUncomplete.push(task.id)
    }
  }

  // Bulk restore from trash
  if (needRestore.length > 0) {
    for (const id of needRestore) {
      await restoreFromTrash(id)
    }
  }

  // Bulk uncomplete
  if (needUncomplete.length > 0) {
    await bulkUncomplete(needUncomplete)
  }
}

// ---------------------------------------------------------------------------
// Phase 2: Target behavior implementations
// ---------------------------------------------------------------------------

/**
 * Inbox: parentPath → "root/#inbox", clear schedule.
 */
async function dropToInbox(taskIds: string[]): Promise<void> {
  if (taskIds.length === 1) {
    if (!reportRefusedMove(await moveTask(taskIds[0]!, "root/#inbox"))) return
    await clearSchedule(taskIds[0]!)
  } else {
    if (!reportRefusedMove(await moveMultiple(taskIds, "root/#inbox"))) return
    for (const id of taskIds) {
      await clearSchedule(id)
    }
  }
}

/**
 * Today: schedule as Default, place at top of Today view.
 * Uses scheduleTask for undo registration + inbox auto-move,
 * then overrides index for top placement.
 */
async function dropToToday(taskIds: string[]): Promise<void> {
  const timestamp = now()

  // Find the minimum startAtIndex to place new items above everything
  const firstEntry = await db.startAtIndex.orderBy("index").first()
  const minIndex = firstEntry ? firstEntry.index : 0

  for (let i = 0; i < taskIds.length; i++) {
    const id = taskIds[i]!
    // scheduleTask registers undo + handles inbox auto-move
    await scheduleTask(id, timestamp, StartAtType.Default, StartAtSubType.Default)
    // Override index to place at top of Today
    await db.startAtIndex.update(`#${id}`, { index: minIndex - 1 - i })
  }
}

/**
 * This Evening: schedule as Default + ThisEvening subType.
 * Uses scheduleTask for undo registration + inbox auto-move.
 */
async function dropToThisEvening(taskIds: string[]): Promise<void> {
  const timestamp = now()

  for (const id of taskIds) {
    const existing = await db.startAtIndex.get(`#${id}`)
    // scheduleTask registers undo + handles inbox auto-move
    await scheduleTask(
      id,
      existing?.startAt ?? timestamp,
      StartAtType.Default,
      StartAtSubType.ThisEvening,
    )
  }
}

/**
 * Anyday: remove startAtIndex entry (clear schedule).
 * parentPath does NOT change.
 */
async function dropToAnyday(taskIds: string[]): Promise<void> {
  for (const id of taskIds) {
    await clearSchedule(id)
  }
}

/**
 * Someday: startAt = now(), type = Someday.
 * parentPath does NOT change.
 */
async function dropToSomeday(taskIds: string[]): Promise<void> {
  const timestamp = now()
  for (const id of taskIds) {
    await scheduleTask(
      id,
      timestamp,
      StartAtType.Someday,
      StartAtSubType.Default,
    )
  }
}

/**
 * Logbook: mark as completed.
 */
async function dropToLogbook(taskIds: string[]): Promise<void> {
  // Projects and lists dropped here are completed the container way (asking what to do with their
  // open to-dos); the shared task ops refuse them
  const rest = await setContainerStatuses(taskIds, ObjectStatus.Completed)
  if (rest.length === 1) {
    await completeTask(rest[0]!)
  } else if (rest.length > 1) {
    await bulkComplete(rest)
  }
}

/**
 * Trash: soft-delete.
 */
async function dropToTrash(taskIds: string[]): Promise<void> {
  if (taskIds.length === 1) {
    await deleteTask(taskIds[0]!)
  } else {
    await bulkDelete(taskIds)
  }
}

/**
 * Container (Project/List/Area): move to container.
 * Position respects the "Drop position" user setting (#move-object-land-behavior):
 *   - "before-first-section" (default): before the first Section in the container
 *   - "end-of-list": bottom of all items
 *   - "after-last-task": after the last Task, before any Sections
 */
async function dropToContainer(
  taskIds: string[],
  containerId: string,
): Promise<void> {
  const newParentPath = `root/${containerId}`
  const insertPosition = await resolveDropPosition(newParentPath)
  const opts = { insertPosition }

  reportRefusedMove(
    taskIds.length === 1
      ? await moveTask(taskIds[0]!, newParentPath, opts)
      : await moveMultiple(taskIds, newParentPath, opts),
  )
}

/**
 * A move the op refused (the target is gone, or may not hold what was dropped) moved nothing:
 * tell the user why, with the app's error toast. Returns whether the move happened.
 */
function reportRefusedMove(result: { ok: true } | { ok: false; error: string }): boolean {
  if (result.ok) return true
  toast.error(result.error)
  return false
}

/**
 * Read the user's "Drop position" setting and resolve it to an InsertPosition
 * by examining the siblings in the target container.
 */
async function resolveDropPosition(
  parentPath: string,
): Promise<InsertPosition> {
  // Read setting from DB — default to "before-first-section"
  const settingRow = await db.setting.get("#move-object-land-behavior")
  // Backward compat: old DB values may still say "before-first-headline"
  const raw = settingRow?.value ?? "before-first-section"
  const pref = raw === "before-first-headline" ? "before-first-section" : raw

  if (pref === "end-of-list") return "bottom"

  // Query siblings sorted by index to find sections
  const siblings = await db.totodo
    .where("[parentPath+index]")
    .between([parentPath, -Infinity], [parentPath, Infinity])
    .filter((o) => o.isDeleted === 0)
    .sortBy("index")

  if (siblings.length === 0) return "bottom"

  if (pref === "before-first-section") {
    const firstSection = siblings.find((s) => s.type === ObjectType.Section)
    if (firstSection?.id) return { beforeId: firstSection.id }
    // No sections → bottom
    return "bottom"
  }

  if (pref === "after-last-task") {
    // Find the last Task/non-Section before the first Section
    const firstHlIdx = siblings.findIndex((s) => s.type === ObjectType.Section)
    if (firstHlIdx === -1) return "bottom" // no sections → bottom
    if (firstHlIdx === 0) return { beforeId: siblings[0]!.id as string } // section is first → insert before it
    const lastTaskBeforeHl = siblings[firstHlIdx - 1]!
    return { afterId: lastTaskBeforeHl.id as string }
  }

  return "bottom"
}
